import { useAuthenticatedUser } from '@ifixit/auth-sdk/client';
import type { User } from '@ifixit/auth-sdk';
import { captureException, SentryError } from '@ifixit/sentry';
import {
   type CartWarning,
   type CountryCode,
   type DisplayableError,
   type LanguageCode,
   type StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { performAddToCart } from './use-add-to-cart';
import { useGetCustomerAccessToken } from '../use-get-customer-access-token';
import { getSyncedAttributes } from '../../helpers/attributes';
import {
   clearStoredCartId,
   clearStoredCustomerAccessToken,
   getStoredCartId,
   updateCartId,
} from '../../helpers/storage';
import { getCart, updateCartAttributes } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { useCartKeys } from './use-cart-keys';
import { useTranslations } from '@ifixit/i18n/hooks';
import type { CartMutationType, Cart } from '../../types';
import { useToast } from '@chakra-ui/react';
import { useAppContext } from '@ifixit/app/hooks';

export function useCart() {
   const { data: user } = useAuthenticatedUser();
   const { queryKey } = useCartKeys();
   const getCustomerAccessToken = useGetCustomerAccessToken();
   const {
      client,
      currencyCode: fallbackCurrencyCode,
      country,
      language,
      storeCode,
   } = useShopifyStorefrontClient();
   const isMutating = useIsMutating();
   const { isOnMobileApp } = useAppContext();

   const query = useQuery({
      queryKey,
      enabled: !isMutating,
      refetchOnWindowFocus: 'always', // refetch when switching tabs
      staleTime: 60 * 1000, // 1 minute
      queryFn: async () => {
         const loggedInCartId = user ? getStoredCartId({ user, storeCode }) : null;
         const anonymousCartId = getStoredCartId({ user: null, storeCode });
         const loggedInCart = loggedInCartId
            ? await getCart(client, { cartId: loggedInCartId, country, language })
            : null;
         const anonymousCart = anonymousCartId
            ? await getCart(client, { cartId: anonymousCartId, country, language })
            : null;
         const allUserErrors: DisplayableError[] = [];
         const allValidationErrors: string[] = [];
         const allWarnings: CartWarning[] = [];
         let finalCart = loggedInCart ?? anonymousCart;

         if (anonymousCart && user) {
            let mergedCart = null;

            if (loggedInCart) {
               const { cart, userErrors, validationErrors, warnings } = await performAddToCart({
                  cartId: loggedInCart.id,
                  client,
                  country,
                  lines: buildIfixitCart({
                     cart: anonymousCart,
                     fallbackCurrencyCode,
                     userErrors: [],
                     validationErrors: [],
                     warnings: [],
                  }).lineItems,
                  language,
               });
               mergedCart = cart ?? loggedInCart;
               allUserErrors.push(...userErrors);
               allValidationErrors.push(...validationErrors);
               allWarnings.push(...warnings);
            } else {
               const { cart, userErrors, validationErrors, warnings } =
                  await updateCartBuyerIdentity({
                     client,
                     cartId: anonymousCart.id,
                     country,
                     language,
                     getCustomerAccessToken,
                     storeCode,
                     user,
                  });
               mergedCart = cart;
               allUserErrors.push(...userErrors);
               allValidationErrors.push(...validationErrors);
               allWarnings.push(...warnings);
            }

            if (mergedCart) {
               updateCartId({ cartId: mergedCart.id, storeCode, userId: user.id });
            }
            clearStoredCartId({ user: null, storeCode });
            finalCart = mergedCart;
         }

         const { attributes, needsSync } = getSyncedAttributes({ cart: finalCart, isOnMobileApp });
         if (finalCart && needsSync) {
            const { cart, userErrors, validationErrors, warnings } = await updateCartAttributes(
               client,
               {
                  attributes,
                  cartId: finalCart.id,
                  country,
                  language,
               }
            );
            finalCart = cart ?? null;
            allUserErrors.push(...userErrors);
            allValidationErrors.push(...validationErrors);
            allWarnings.push(...warnings);
         }

         if ((loggedInCartId || anonymousCartId) && finalCart == null) {
            clearStoredCartId({ storeCode, user: user ?? null });
         }

         return buildIfixitCart({
            cart: finalCart,
            fallbackCurrencyCode,
            userErrors: allUserErrors,
            validationErrors: allValidationErrors,
            warnings: allWarnings,
         });
      },
   });
   return query;
}

export async function updateCartBuyerIdentity({
   client,
   cartId,
   country,
   language,
   getCustomerAccessToken,
   storeCode,
   user,
}: {
   client: StorefrontClient;
   cartId: string;
   country: CountryCode;
   language: LanguageCode;
   getCustomerAccessToken: ReturnType<typeof useGetCustomerAccessToken>;
   storeCode: string;
   user: User;
}) {
   const customerAccessToken = await getCustomerAccessToken(user);
   let response = await client.updateCartBuyerIdentity({
      cartId,
      country,
      buyerIdentity: { customerAccessToken: customerAccessToken?.accessToken },
      language,
   });
   if (
      customerAccessToken &&
      response.cartBuyerIdentityUpdate?.cart &&
      response.cartBuyerIdentityUpdate.cart.buyerIdentity.customer == null
   ) {
      // If the token is invalid or expired, clear it from storage and retry.
      clearStoredCustomerAccessToken({ storeCode, user });
      const customerAccessToken = await getCustomerAccessToken(user);
      response = await client.updateCartBuyerIdentity({
         cartId,
         country,
         buyerIdentity: { customerAccessToken: customerAccessToken?.accessToken },
         language,
      });
   }
   if (!response.cartBuyerIdentityUpdate?.cart) {
      throw new SentryError('Failed to update cart buyer identity', { extra: { cartId, user } });
   }
   return {
      cart: response.cartBuyerIdentityUpdate.cart,
      userErrors: response.cartBuyerIdentityUpdate.userErrors || [],
      validationErrors: [],
      warnings: response.cartBuyerIdentityUpdate.warnings || [],
   };
}

function useGetCartWarningDescription() {
   // https://shopify.dev/docs/api/storefront/2024-10/enums/CartWarningCode
   const t = useTranslations('ErrorHandling');
   return (CartWarning: CartWarning) => {
      switch (CartWarning.code) {
         case 'MERCHANDISE_NOT_ENOUGH_STOCK':
         case 'MERCHANDISE_OUT_OF_STOCK': {
            return CartWarning.message;
         }
         default: {
            return t('tryActionAgain');
         }
      }
   };
}

export function useCartToasts({ title }: { title: string }) {
   const t = useTranslations();
   const toast = useToast();
   const getCartWarningDescription = useGetCartWarningDescription();

   const showCartWarnings = ({
      cart,
      type,
      variables,
   }: {
      cart: Cart;
      type: CartMutationType;
      variables: unknown;
   }) => {
      // Show a single toast for user errors
      if (cart.userErrors && cart.userErrors.length > 0) {
         const msg = `Cart user errors: ${type}`;
         console.error(msg, cart.userErrors);
         captureException(
            new SentryError(msg, {
               extra: {
                  'User Errors': JSON.stringify(cart.userErrors, null, 2),
                  Variables: JSON.stringify(variables, null, 2),
               },
            })
         );
         toast({
            id: `${type}-user-error`,
            status: 'error',
            title,
            description: t('ErrorHandling.tryActionAgain'),
            isClosable: true,
            variant: 'subtle',
            position: 'bottom-right',
         });
      }

      // Show validation error toasts if there are any
      if (cart.validationErrors && cart.validationErrors.length > 0) {
         cart.validationErrors.forEach(error => {
            toast({
               id: `${type}-validation-error-${error}`,
               status: 'error',
               title: error,
               isClosable: true,
               variant: 'subtle',
               position: 'bottom-right',
            });
         });
      }

      // Show warnings toasts if there are any
      if (cart.warnings && cart.warnings.length > 0) {
         cart.warnings.forEach(warning => {
            toast({
               id: `${type}-warning-${warning.message}`,
               status: 'warning',
               title,
               description: getCartWarningDescription(warning),
               isClosable: true,
               variant: 'subtle',
               position: 'bottom-right',
            });
         });
      }
   };

   const showErrorToast = () => {
      toast.closeAll();
      toast({
         id: 'generic-cart-error',
         status: 'error',
         title,
         description: t('ErrorHandling.tryActionAgain'),
         isClosable: true,
         variant: 'subtle',
         position: 'bottom-right',
      });
   };

   return { showCartWarnings, showErrorToast };
}
